import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Mountain } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const NAV_ITEMS = [
  { key: "sector", hash: "#sector" },
  { key: "process", hash: "#process" },
  { key: "transparency", hash: "#transparency" },
  { key: "subscribe", hash: "#subscribe" },
];

const LangButton = ({ code, active, onClick }) => (
  <button
    type="button"
    data-testid={`lang-switch-${code}`}
    onClick={() => onClick(code)}
    className={
      "px-2.5 py-1 text-xs font-semibold tracking-widest uppercase border transition-colors " +
      (active
        ? "bg-white text-[#0B2545] border-white"
        : "bg-transparent text-white/80 border-white/30 hover:text-white hover:border-white")
    }
  >
    {code === "ar" ? "ES" : "EN"}
  </button>
);

const Header = () => {
  const { t, lang, switchLang, localePath } = useI18n();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#0B2545] text-white border-b border-slate-700 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between">
          <Link
            to={localePath("/")}
            data-testid="header-logo-link"
            onClick={close}
            className="flex items-center gap-3"
          >
            <span className="w-9 h-9 flex items-center justify-center bg-[#C85A32] text-white">
              <Mountain size={18} strokeWidth={2.25} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-lg font-bold tracking-tight">
                {t("brand.name")}
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-300 hidden sm:inline">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-8"
            data-testid="header-desktop-nav"
            aria-label="Primary"
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={`${localePath("/")}${item.hash}`}
                data-testid={`nav-link-${item.key}`}
                className="text-sm font-medium text-slate-200 hover:text-white"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-400 mr-1">
              {t("language.label")}
            </span>
            <LangButton code="ar" active={lang === "ar"} onClick={switchLang} />
            <LangButton code="en" active={lang === "en"} onClick={switchLang} />
          </div>

          <button
            type="button"
            data-testid="header-mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/25 text-white"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-700 bg-[#0B2545]" data-testid="header-mobile-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4">
            <nav className="flex flex-col gap-3" aria-label="Mobile">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.key}
                  href={`${localePath("/")}${item.hash}`}
                  data-testid={`mobile-nav-link-${item.key}`}
                  onClick={close}
                  className="text-base font-medium text-slate-100 py-2 border-b border-slate-700"
                >
                  {t(`nav.${item.key}`)}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-2 pt-2">
              <span className="text-[11px] uppercase tracking-widest text-slate-400 mr-1">
                {t("language.label")}
              </span>
              <LangButton
                code="ar"
                active={lang === "ar"}
                onClick={(c) => {
                  switchLang(c);
                  close();
                }}
              />
              <LangButton
                code="en"
                active={lang === "en"}
                onClick={(c) => {
                  switchLang(c);
                  close();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
